/** Client API du module plannings & validation des heures. */
import { apiClient } from './client'

const BASE = '/api/pexitalk/planning'

export type BlockKind = 'work' | 'meeting' | 'break' | 'lunch' | 'training' | 'coaching' | 'other'

export interface ScheduleBlock {
  id?: number
  start: string          // 'HH:MM[:SS]'
  end: string
  kind: BlockKind
  kind_label?: string
  label: string
  duration_minutes?: number
}

export interface Schedule {
  id: number
  name: string
  notes: string
  /** {'4': '21:00'} — fin de journée spécifique par jour (lundi=0…dimanche=6). */
  weekday_end_overrides: Record<string, string>
  is_active: boolean
  blocks: ScheduleBlock[]
  assigned_count?: number
  updated_at: string
}

export interface AssignmentRow {
  user_id: number
  display_name: string
  role: string
  schedule_id: number | null
  schedule_name: string | null
}

export interface DayReviewRow {
  user_id: number
  display_name: string
  role: string
  schedule_id: number
  schedule_name: string
  planned_seconds: number
  planned_hms: string
  planned_start: string | null
  worked_seconds: number
  worked_hms: string
  breakdown: Record<string, number>
  first_login: string | null
  late_seconds: number | null
  review: WorkDayReview | null
}

export interface WorkDayReview {
  id: number
  user_id: number
  date: string
  status: 'a_valider' | 'valide'
  off_type: '' | 'off' | 'conge' | 'ferie'
  system_seconds: number
  status_breakdown: Record<string, number>
  late_seconds: number
  pc_down_seconds: number
  adjustment_seconds: number
  shift_start: string | null
  shift_end: string | null
  majoration_pct: string
  note: string
  final_seconds: number
  majored_seconds: number
  validated_by_id: number | null
  validated_at: string | null
}

export interface Holiday { id: number; date: string; label: string }

export interface TotalsRow {
  user_id: number
  display_name: string
  role: string
  days: number
  final_seconds: number
  final_hms: string
  majored_seconds: number
  majored_hms: string
  late_seconds: number
  pc_down_seconds: number
  off_days: number
  conge_days: number
  ferie_days: number
}

export const PlanningAPI = {
  // ── Mon planning (tous les rôles) ──
  my(): Promise<{ schedule: Schedule | null; version: string }> {
    return apiClient.get(`${BASE}/my/`).then((r) => r.data)
  },

  // ── Gestion (owner + admins autorisés) ──
  schedules(): Promise<Schedule[]> {
    return apiClient.get(`${BASE}/schedules/`).then((r) => r.data.results || r.data)
  },
  createSchedule(payload: Partial<Schedule>): Promise<Schedule> {
    return apiClient.post(`${BASE}/schedules/`, payload).then((r) => r.data)
  },
  updateSchedule(id: number, payload: Partial<Schedule>): Promise<Schedule> {
    return apiClient.put(`${BASE}/schedules/${id}/`, payload).then((r) => r.data)
  },
  deleteSchedule(id: number): Promise<void> {
    return apiClient.delete(`${BASE}/schedules/${id}/`).then(() => undefined)
  },

  assignments(): Promise<AssignmentRow[]> {
    return apiClient.get(`${BASE}/assignments/`).then((r) => r.data.rows)
  },
  assign(user_id: number, schedule: number | null): Promise<void> {
    return apiClient.post(`${BASE}/assignments/`, { user_id, schedule }).then(() => undefined)
  },

  review(date: string): Promise<{ date: string; holiday: string | null; rows: DayReviewRow[] }> {
    return apiClient.get(`${BASE}/review/`, { params: { date } }).then((r) => r.data)
  },
  validateDay(payload: {
    user_id: number; date: string; off_type?: string
    pc_down_seconds?: number; adjustment_seconds?: number
    shift_start?: string | null; shift_end?: string | null
    majoration_pct?: string | number; note?: string; reset?: boolean
  }): Promise<WorkDayReview> {
    return apiClient.post(`${BASE}/review/validate/`, payload).then((r) => r.data)
  },

  holidays(): Promise<Holiday[]> {
    return apiClient.get(`${BASE}/holidays/`).then((r) => r.data.results || r.data)
  },
  addHoliday(date: string, label: string): Promise<Holiday> {
    return apiClient.post(`${BASE}/holidays/`, { date, label }).then((r) => r.data)
  },
  deleteHoliday(id: number): Promise<void> {
    return apiClient.delete(`${BASE}/holidays/${id}/`).then(() => undefined)
  },

  totals(from: string, to: string, user?: number): Promise<{
    from: string; to: string; rows: TotalsRow[]
    total_final_seconds: number; total_final_hms: string
    total_majored_seconds: number; total_majored_hms: string
  }> {
    return apiClient.get(`${BASE}/totals/`, { params: { from, to, user: user || undefined } }).then((r) => r.data)
  },
}

/** Libellés/couleurs des types de blocs (affichage planning + éditeur). */
export const BLOCK_KINDS: Array<{ value: BlockKind; label: string }> = [
  { value: 'work', label: 'Travail' },
  { value: 'meeting', label: 'Réunion' },
  { value: 'break', label: 'Pausette' },
  { value: 'lunch', label: 'Pause déjeuner' },
  { value: 'training', label: 'Formation' },
  { value: 'coaching', label: 'Coaching' },
  { value: 'other', label: 'Autre' },
]

export function fmtHms(seconds: number): string {
  const s = Math.max(0, Math.round(seconds || 0))
  const h = Math.floor(s / 3600)
  const m = Math.floor((s % 3600) / 60)
  const sec = s % 60
  return `${h}h ${String(m).padStart(2, '0')}m ${String(sec).padStart(2, '0')}s`
}

export function fmtHm(seconds: number): string {
  const s = Math.max(0, Math.round(seconds || 0))
  const h = Math.floor(s / 3600)
  const m = Math.floor((s % 3600) / 60)
  return h ? `${h}h ${String(m).padStart(2, '0')}m` : `${m}m`
}
